<template>
  <MainHeader />
  <MainSidebar />

  <div class="main-content bg_gray d-flex flex-column transition overflow-x-hidden">
    <BreadcrumbMenu
      :pageTitle="$t('pos.waiting_queue')"
      :items="breadcrumbItems"
      :showHome="false"
    />

    <div class="row g-4 mb-25">
      <div class="col-xl-8">
        <div class="card border-0 shadow-none rounded-1 h-100">
          <div class="card-body p-xl-40">
            <div class="row g-3 align-items-end">
              <div class="col-lg-4">
                <label class="form-label fw-semibold text-title">{{ $t("pos.warehouse") }}</label>
                <select
                  v-model.number="selectedWarehouseId"
                  class="form-select h-55 bg-white shadow-none"
                  :disabled="warehouseLocked"
                >
                  <option :value="0">{{ $t("pos.all_warehouses") }}</option>
                  <option
                    v-for="warehouse in warehouses"
                    :key="warehouse.id"
                    :value="warehouse.id"
                  >
                    {{ warehouse.name }}
                  </option>
                </select>
              </div>

              <div class="col-lg-5">
                <label class="form-label fw-semibold text-title">{{ $t("pos.search_queue") }}</label>
                <input
                  v-model.trim="searchQuery"
                  type="text"
                  class="form-control h-55 bg-white shadow-none"
                  :placeholder="$t('pos.search_waiting_number_invoice_or_customer')"
                />
              </div>

              <div class="col-lg-3">
                <button
                  type="button"
                  class="btn style-two w-100 h-55 queue-refresh-btn"
                  :disabled="isRefreshing"
                  @click="refreshQueueData"
                >
                  <span
                    v-if="isRefreshing"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <i
                    v-else
                    class="ri-refresh-line me-1"
                  ></i>
                  {{ $t("pos.refresh") }}
                </button>
              </div>
            </div>

            <div class="queue-summary-grid mt-4">
              <article class="queue-summary-card tone-pending">
                <span>{{ $t("pos.pending_tickets") }}</span>
                <strong>{{ pendingOrders.length }}</strong>
                <small>{{ $t("pos.waiting_for_counter_handoff") }}</small>
              </article>
              <article class="queue-summary-card tone-completed">
                <span>{{ $t("pos.completed_today") }}</span>
                <strong>{{ completedOrders.length }}</strong>
                <small>{{ $t("pos.ready_or_handed_out") }}</small>
              </article>
              <article class="queue-summary-card tone-counter">
                <span>{{ $t("pos.active_counter") }}</span>
                <strong>{{ activeWarehouseLabel }}</strong>
                <small>{{ $t("pos.queue_numbers_reset_per_warehouse") }}</small>
              </article>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-4">
        <div class="card border-0 shadow-none rounded-1 h-100">
          <div class="card-body p-xl-40">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div>
                <span class="text-uppercase fs-12 fw-semibold text-muted">{{ $t("pos.pickup_board") }}</span>
                <h5 class="mb-0 mt-1 fw-bold">{{ $t("pos.recently_completed") }}</h5>
              </div>
              <router-link
                :to="pickupBoardLink"
                class="btn btn-outline-primary btn-sm"
              >
                {{ $t("pos.open_board") }}
              </router-link>
            </div>

            <div v-if="recentCompletedOrders.length" class="completed-mini-list">
              <article
                v-for="order in recentCompletedOrders"
                :key="`completed-${order.id}`"
                class="completed-mini-card"
              >
                <div>
                  <small>{{ $t("pos.waiting_no") }} #{{ getQueueLabel(order) }}</small>
                  <strong>{{ order.reference_no }}</strong>
                </div>
                <span>{{ formatDateTime(order.queue_completed_at || order.created_at) }}</span>
              </article>
            </div>

            <div v-else class="empty-mini-state">
              <i class="ri-notification-off-line"></i>
              <p class="mb-0">{{ $t("pos.no_completed_pickup_orders_for_counter") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card border-0 shadow-none rounded-1">
      <div class="card-body p-xl-40">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <span class="text-uppercase fs-12 fw-semibold text-muted">{{ $t("pos.counter_queue") }}</span>
            <h4 class="mb-0 mt-1 fw-bold">{{ $t("pos.pending_invoices") }}</h4>
          </div>
          <span class="text-muted fs-13">
            {{ $t("pos.ticket_count", { count: filteredPendingOrders.length }) }}
          </span>
        </div>

        <div v-if="isInitialLoading" class="queue-loading-state">
          <div class="spinner-border text-primary mb-3" role="status"></div>
          <p class="mb-0 text-muted">{{ $t("pos.loading_waiting_queue") }}</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table queue-table align-middle mb-0">
            <thead>
              <tr>
                <th>{{ $t("pos.waiting_no") }}</th>
                <th>{{ $t("pos.invoice") }}</th>
                <th>{{ $t("pos.customer") }}</th>
                <th>{{ $t("pos.warehouse") }}</th>
                <th>{{ $t("pos.items") }}</th>
                <th>{{ $t("pos.total") }}</th>
                <th>{{ $t("pos.payment_method") }}</th>
                <th>{{ $t("pos.created") }}</th>
                <th class="text-end">{{ $t("common.action") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in filteredPendingOrders"
                :key="order.id"
              >
                <td>
                  <span class="queue-pill queue-pill-pending">
                    #{{ getQueueLabel(order) }}
                  </span>
                </td>
                <td class="fw-semibold text-title">{{ order.reference_no }}</td>
                <td>{{ order.customer_name || $t("pos.walk_in_customer") }}</td>
                <td>{{ order.warehouse_name || $t("pos.main_counter") }}</td>
                <td>{{ getItemsCount(order) }}</td>
                <td>{{ stateStore.formatPrice(order.grand_total || 0) }}</td>
                <td>{{ formatPaymentMethod(order.paid_by) }}</td>
                <td>{{ formatDateTime(order.created_at) }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-success btn-sm px-3"
                    :disabled="updatingSaleId === order.id"
                    @click="markCompleted(order)"
                  >
                    <span
                      v-if="updatingSaleId === order.id"
                      class="spinner-border spinner-border-sm me-1"
                    ></span>
                    {{ $t("pos.mark_completed") }}
                  </button>
                </td>
              </tr>

              <tr v-if="!filteredPendingOrders.length">
                <td colspan="9" class="text-center py-5">
                  <div class="empty-table-state">
                    <i class="ri-inbox-unarchive-line"></i>
                    <h6 class="fw-bold mb-1">{{ $t("pos.no_pending_invoices") }}</h6>
                    <p class="mb-0 text-muted">{{ $t("pos.new_paid_pos_orders_appear") }}</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import Swal from "sweetalert2";

import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";
import posService, { type PosQueueSale } from "../../services/pos";
import warehouseService, { type Warehouse } from "../../services/warehouse";
import stateStore from "../../utils/store";
import { formatQueueLabel } from "../../utils/posQueue";
import { useCurrentUser } from "../../composables/useCurrentUser";

export default defineComponent({
  name: "PosQueuePage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    MainFooter,
  },
  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();
    const { fetchCurrentUser } = useCurrentUser();

    const breadcrumbItems = computed(() => [
      { label: "POS", to: "/pos" },
      { label: t("pos.waiting_queue") },
    ]);

    const warehouses = ref<Warehouse[]>([]);
    const selectedWarehouseId = ref(0);
    const profileWarehouseId = ref<number | null>(null);
    const searchQuery = ref("");
    const isInitialLoading = ref(true);
    const isRefreshing = ref(false);
    const updatingSaleId = ref<number | null>(null);
    const pendingOrders = ref<PosQueueSale[]>([]);
    const completedOrders = ref<PosQueueSale[]>([]);
    const queueReady = ref(false);
    const activeLocale = computed(() =>
      String(locale.value || "en").toLowerCase().startsWith("km")
        ? "km-KH"
        : "en-US"
    );

    const warehouseLocked = computed(() => profileWarehouseId.value !== null);

    const activeWarehouseLabel = computed(() => {
      if (!selectedWarehouseId.value) {
        return t("pos.all_counters");
      }

      return (
        warehouses.value.find((warehouse) => warehouse.id === selectedWarehouseId.value)
          ?.name || t("pos.selected_counter")
      );
    });

    const pickupBoardLink = computed(() => ({
      name: "PosPickupDisplayPage",
      query: selectedWarehouseId.value
        ? { warehouse_id: String(selectedWarehouseId.value) }
        : {},
    }));

    const filteredPendingOrders = computed(() => {
      const keyword = searchQuery.value.trim().toLowerCase();
      if (!keyword) {
        return pendingOrders.value;
      }

      return pendingOrders.value.filter((order) => {
        const haystack = [
          getQueueLabel(order),
          order.reference_no,
          order.customer_name,
          order.warehouse_name,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(keyword);
      });
    });

    const recentCompletedOrders = computed(() =>
      completedOrders.value.slice(0, 3)
    );

    const fetchWarehouses = async () => {
      try {
        warehouses.value = await warehouseService.getWarehouses();
      } catch (error) {
        console.error("Unable to load warehouses:", error);
      }
    };

    const fetchQueueData = async (mode: "initial" | "refresh" = "initial") => {
      if (mode === "refresh") {
        isRefreshing.value = true;
      } else {
        isInitialLoading.value = true;
      }

      try {
        const warehouse_id = selectedWarehouseId.value || undefined;
        const [pending, completed] = await Promise.all([
          posService.getQueueOrders({
            warehouse_id,
            status: "pending",
          }),
          posService.getQueueOrders({
            warehouse_id,
            status: "completed",
            limit: 20,
          }),
        ]);

        pendingOrders.value = Array.isArray(pending) ? pending : [];
        completedOrders.value = Array.isArray(completed) ? completed : [];
      } catch (error) {
        console.error("Unable to load POS queue:", error);
        await Swal.fire({
          icon: "error",
          title: t("pos.queue_unavailable"),
          text: t("pos.unable_to_load_waiting_queue"),
        });
      } finally {
        isInitialLoading.value = false;
        isRefreshing.value = false;
      }
    };

    const refreshQueueData = () => fetchQueueData("refresh");

    const markCompleted = async (order: PosQueueSale) => {
      updatingSaleId.value = order.id;

      try {
        await posService.updateQueueStatus(order.id, "completed");
        await fetchQueueData();
        await Swal.fire({
          icon: "success",
          title: t("pos.updated"),
          text: t("pos.waiting_number_marked_completed", {
            queue: getQueueLabel(order),
          }),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Unable to complete queue order:", error);
        await Swal.fire({
          icon: "error",
          title: t("pos.update_failed"),
          text: t("pos.unable_to_update_waiting_invoice"),
        });
      } finally {
        updatingSaleId.value = null;
      }
    };

    const getQueueLabel = (order: PosQueueSale) =>
      order.queue_label || formatQueueLabel(order.queue_number);

    const getItemsCount = (order: PosQueueSale) =>
      Array.isArray(order.items)
        ? order.items.reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0
          )
        : 0;

    const formatPaymentMethod = (value?: string | null) => {
      const normalized = String(value || "cash")
        .replace(/[\s-]+/g, "_")
        .trim()
        .toLowerCase();

      const keyMap: Record<string, string> = {
        cash: "payment_cash",
        card: "payment_card",
        khqr: "payment_khqr",
        aba: "payment_aba",
        aclida: "payment_aclida",
        wing: "payment_wing",
        gift_card: "payment_gift_card",
        bank: "payment_bank",
      };

      return keyMap[normalized]
        ? t(`pos.${keyMap[normalized]}`)
        : normalized.replace(/_/g, " ").toUpperCase();
    };

    const formatDateTime = (value?: string | null) => {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      return date.toLocaleString(activeLocale.value, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    watch(selectedWarehouseId, () => {
      if (!queueReady.value) {
        return;
      }
      fetchQueueData();
    });

    onMounted(async () => {
      const initialWarehouseFromQuery = Number(route.query.warehouse_id || 0) || 0;
      await fetchWarehouses();

      const currentUser = await fetchCurrentUser(true);
      if (currentUser?.warehouse_id) {
        profileWarehouseId.value = Number(currentUser.warehouse_id);
      }

      selectedWarehouseId.value =
        profileWarehouseId.value || initialWarehouseFromQuery || 0;

      queueReady.value = true;
      await fetchQueueData();
    });

    return {
      breadcrumbItems,
      warehouses,
      selectedWarehouseId,
      warehouseLocked,
      activeWarehouseLabel,
      pickupBoardLink,
      searchQuery,
      isInitialLoading,
      isRefreshing,
      updatingSaleId,
      pendingOrders,
      completedOrders,
      recentCompletedOrders,
      filteredPendingOrders,
      fetchQueueData,
      refreshQueueData,
      markCompleted,
      getQueueLabel,
      getItemsCount,
      formatPaymentMethod,
      formatDateTime,
      stateStore,
    };
  },
});
</script>

<style scoped>
.queue-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.queue-summary-card {
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
}

.queue-summary-card span,
.queue-summary-card small {
  display: block;
}

.queue-summary-card span {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.queue-summary-card strong {
  display: block;
  margin: 10px 0 6px;
  font-size: 28px;
  color: #0f172a;
}

.queue-summary-card small {
  color: #475569;
}

.tone-pending {
  background: linear-gradient(135deg, #fff7ed 0%, #ffffff 100%);
}

.tone-completed {
  background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%);
}

.tone-counter {
  background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
}

.completed-mini-list {
  display: grid;
  gap: 12px;
}

.completed-mini-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.completed-mini-card small,
.completed-mini-card strong,
.completed-mini-card span {
  display: block;
}

.completed-mini-card small {
  color: #16a34a;
  font-weight: 700;
}

.completed-mini-card strong {
  color: #0f172a;
}

.completed-mini-card span {
  color: #64748b;
  font-size: 12px;
  text-align: right;
}

.queue-loading-state,
.empty-mini-state,
.empty-table-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-mini-state {
  min-height: 220px;
  color: #64748b;
}

.empty-mini-state i,
.empty-table-state i {
  font-size: 32px;
  color: #94a3b8;
  margin-bottom: 10px;
}

.queue-table thead th {
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.queue-table tbody td {
  padding-top: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #f1f5f9;
}

.queue-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.queue-pill-pending {
  background: #fff7ed;
  color: #c2410c;
}

.queue-refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1;
  padding-inline: 12px;
}

.queue-refresh-btn i,
.queue-refresh-btn .spinner-border {
  flex-shrink: 0;
}

@media (max-width: 991px) {
  .queue-summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
